import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { DASTicketDetails } from '../../../../api/types/dasTickets.types';
import { useApprovalActions } from '../../../../hooks/api/das/useApprovalActions';
import type { CancelRequestRequest } from '../../../../api/types/das/approvalActions.types';

interface CancelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: DASTicketDetails;
  onSuccess?: () => void;
}

export const CancelRequestModal: React.FC<CancelRequestModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { cancel } = useApprovalActions();

  if (!isOpen) return null;

  // Use ticket number for confirmation
  const isConfirmed = confirmText.toUpperCase() === ticket.ticketNo.toUpperCase();

  const handleCancelClick = () => {
    if (!reason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    if (!isConfirmed) {
      setError(`Please type "${ticket.ticketNo}" to confirm`);
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);
    setError(null);

    const request: CancelRequestRequest = {
      reason: reason.trim(),
      comments: comments.trim() || undefined,
      rowVersion: parseInt(ticket.rowVersion || '0', 10),
    };

    try {
      const result = await cancel(String(ticket.id), request);
      if (result) {
        onSuccess?.();
        onClose();
      } else {
        setError('Failed to cancel request. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isSubmitting && !showConfirmDialog) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        padding: '1rem',
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '0.75rem',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '0.5rem',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconSystem.x size={20} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Cancel Request
              </h2>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {ticket.ticketNo}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              opacity: isSubmitting ? 0.5 : 1,
            }}
            title="Close"
          >
            <IconSystem.x size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem', overflow: 'auto', flex: 1 }}>
          {/* Warning Card */}
          <div
            style={{
              background: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.2)',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <IconSystem.alert size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#dc2626', fontWeight: 600 }}>
                  This action cannot be undone
                </p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  Cancelling this request will permanently close it. You will need to create a new request if needed.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.2)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <IconSystem.alert size={18} style={{ color: '#dc2626' }} />
              <span style={{ fontSize: '0.875rem', color: '#dc2626' }}>{error}</span>
            </div>
          )}

          {/* Reason Field */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              Reason for Cancellation <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you cancelling this request?"
              disabled={isSubmitting}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '0.75rem',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                background: 'var(--bg-primary)',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Comments Field */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              Additional Comments (Optional)
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any additional notes..."
              disabled={isSubmitting}
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '0.75rem',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                background: 'var(--bg-primary)',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Confirmation Field */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              Type <span style={{ color: '#dc2626', fontFamily: 'monospace' }}>{ticket.ticketNo}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type '${ticket.ticketNo}' to confirm`}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${isConfirmed ? '#10b981' : 'var(--border-color)'}`,
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                background: 'var(--bg-primary)',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'transparent',
              border: '1px solid var(--border-color)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--text-primary)',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            Keep Request
          </button>
          <button
            onClick={handleCancelClick}
            disabled={isSubmitting || !reason.trim() || !isConfirmed}
            style={{
              padding: '0.625rem 1.25rem',
              background: isSubmitting || !reason.trim() || !isConfirmed ? '#9ca3af' : '#dc2626',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'white',
              cursor: isSubmitting || !reason.trim() || !isConfirmed ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {isSubmitting ? (
              <>
                <span
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                Cancelling...
              </>
            ) : (
              <>
                <IconSystem.x size={16} />
                Cancel Request
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(220, 38, 38, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconSystem.alert size={20} style={{ color: '#dc2626' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Confirm Cancellation
              </h3>
            </div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Are you sure you want to cancel request <strong style={{ color: 'var(--text-primary)' }}>{ticket.ticketNo}</strong>?
            </p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.8125rem', color: '#dc2626' }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                Go Back
              </button>
              <button
                onClick={handleConfirmCancel}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <IconSystem.x size={16} />
                Yes, Cancel Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CancelRequestModal;
